import { EnvironmentInfo } from "@azure/communication-calling";
import { CallState } from "@azure/communication-react";
import { isUnsupportedEnvironment } from "./Utils";
import { isInCall, isInLobbyOrConnecting } from "./callUtils";
import { PcpCallCompositePage } from "../interfaces";


const REMOTE_PSTN_USER_HUNG_UP = 560000;
const REMOVED_FROM_CALL_SUB_CODES = [5000, 5300, REMOTE_PSTN_USER_HUNG_UP];

enum CallEndReasons {
    LEFT_CALL,
    ACCESS_DENIED,
    REMOVED_FROM_CALL,
    BAD_REQUEST
}

const getCallEndReason = (call: CallState): CallEndReasons => {
    if (call.callEndReason?.subCode && REMOVED_FROM_CALL_SUB_CODES.includes(call.callEndReason.subCode)) {
        return CallEndReasons.REMOVED_FROM_CALL;
    }

    // If the call end reason code is 400, the call is ended due to a bad request. Keep this line at the bottom right before returning normal left call to catch the scenarios not including the ones above.
    if (call.callEndReason?.code === 400) {
        return CallEndReasons.BAD_REQUEST;
    }

    if (call.callEndReason) {
        // No error codes match, assume the user simply left the call regularly
        return CallEndReasons.LEFT_CALL;
    }

    throw new Error('No matching call end reason');
};


/**
 * type definition for conditional-compilation
 */
type GetCallCompositePageFunction = ((
    call: CallState | undefined,
    previousCall: CallState | undefined
) => PcpCallCompositePage) &
    ((
        call: CallState | undefined,
        previousCall: CallState | undefined,
        isReturningFromBreakoutRoom?: boolean,
        /* @conditional-compile-remove(unsupported-browser) */ unsupportedBrowserInfo?: {
            environmentInfo?: EnvironmentInfo;
            unsupportedBrowserVersionOptedIn?: boolean;
        }
    ) => PcpCallCompositePage);
/**
 * Get the current call composite page based on the current call composite state
 *
 * @param Call - The current call state
 * @param previousCall - The state of the most recent previous call that has ended.
 *
 * @remarks - The previousCall state is needed to determine if the call has ended.
 * When the call ends a new call object is created, and so we must lookback at the
 * previous call state to understand how the call has ended. If there is no previous
 * call we know that this is a fresh call and can display the configuration page.
 *
 * @private
 */
export const getCallCompositePage: GetCallCompositePageFunction = (
    call,
    previousCall?,
    isReturningFromBreakoutRoom?: boolean,
    unsupportedBrowserInfo?: {
        environmentInfo?: EnvironmentInfo;
        unsupportedBrowserVersionOptedIn?: boolean;
    }
): PcpCallCompositePage => {
    if (
        isUnsupportedEnvironment(
            unsupportedBrowserInfo?.environmentInfo,
            unsupportedBrowserInfo?.unsupportedBrowserVersionOptedIn
        )
    ) {
        // The Call Composite is unable to run because the browser or platform environment does not meet the minimum requirements for the Azure Communication Services Calling SDK.
        return 'unsupportedEnvironment';
    }

    if (isReturningFromBreakoutRoom) {
        return 'returningFromBreakoutRoom';
    }

    if (call) {
        // Must check for ongoing call *before* looking at any previous calls.
        // If the composite completes one call and joins another, the previous calls
        // will be populated, but not relevant for determining the page.

        // `_isInLobbyOrConnecting` needs to be checked first because `_isInCall` also returns true when call is in lobby.
        if (isInLobbyOrConnecting(call?.state)) {
            return 'lobby';
            // `LocalHold` needs to be checked before `isInCall` since it is also a state that's considered in call.
        } else if (call?.state === 'LocalHold') {
            return 'hold';
        } else if (call?.state === 'Disconnecting') {
            return 'leaving';
        } else if (isInCall(call?.state)) {
            return 'call';
        } else {
            // When the call object has been constructed after clicking , but before 'connecting' has been
            // set on the call object, we continue to show the configuration screen.
            // The call object does not correctly reflect local device state until `call.state` moves to `connecting`.
            // Moving to the 'lobby' page too soon leads to components that depend on the `call` object to show incorrect
            // transitional state.
            return 'configuration';
        }
    }

    if (previousCall) {
        // debugger;
        const reason = getCallEndReason(previousCall);
        switch (reason) {
            case CallEndReasons.ACCESS_DENIED:
                return 'accessDeniedTeamsMeeting';
            case CallEndReasons.REMOVED_FROM_CALL:
                return 'removedFromCall';
            case CallEndReasons.BAD_REQUEST:
                return 'badRequest';
            case CallEndReasons.LEFT_CALL:
                if (previousCall.diagnostics.network.latest.noNetwork) {
                    return 'joinCallFailedDueToNoNetwork';
                }
                return 'leftCall';
        }
    }

    // No call state - show starting page (configuration)
    return 'configuration';
};
